import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { message } from 'antd';
import { HubServiceState } from '@kemu-io/kemu-core/types';
import { HelpServicePanelRef } from '../types';
import styles from './HubServiceHelpContent.module.css';
import globals from '@common/globals';
import MarkdownViewer from '@components/MarkdownViewer/MarkdownViewer';
import { useTranslation, useWidgetState } from '@hooks/index';
import { PublicationDocsRelativePathPrefix, SELF_NAVIGATION_SUFFIX } from '@common/constants';
import marketplaceApi from '@src/api/marketplace/marketplaceApi';

type Props = {
  serviceName: string;
  serviceVersion: string;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  onDraggingKemuSnippet: (isDragging: boolean) => void;
  onLoadingContent: (isLoading: boolean) => void;
  onShowBackButton: (show: boolean) => void;
}


const marketplacePublicationUrl = globals.MARKETPLACE_PUBLIC_URL;
const relativePathPrefix = PublicationDocsRelativePathPrefix;

const HubServiceHelpContent = forwardRef<HelpServicePanelRef, Props>((props, ref) => {
  const { serviceName, serviceVersion, recipeId, thingRecipeId, widgetId, onDraggingKemuSnippet, onLoadingContent, onShowBackButton } = props;
  const docsUrlRef = useRef<string | null>(null);
  const { state } = useWidgetState<HubServiceState>(recipeId, thingRecipeId, widgetId);
  const [basePublicUrl, setBasePublicUrl] = useState<string | null>(null);
  const contentHistoryRef = useRef<string[]>([]);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const t = useTranslation('ServiceHelpPanel');
  const [currentContent, setCurrentContent] = useState<string | null>(null);
  const [serviceDocsData, setServiceDocsData] = useState<{ docs: string; organizationId: string, serviceName: string } | null>(null);

  useImperativeHandle(ref, () => ({
    /**
     * Pops the last content from the history and sets it as the current content
     */
    popContentHistory: () => {
      contentHistoryRef.current.pop();
      setCurrentContent(contentHistoryRef.current[contentHistoryRef.current.length - 1]);
    },
    renderCustomContent: (content: string) => {
      contentHistoryRef.current = [content];
      setCurrentContent(content);
    },
  }), []);

  const setHelpContent = useCallback((md: string, docsUrl: string) => {
    docsUrlRef.current = docsUrl;
    contentHistoryRef.current.push(md);
    setCurrentContent(md);
  }, []);

  /**
   * Fetches service docs and author organization info in a single request.
   * This eliminates the need for separate requests to get organization ID and content.
   */
  const fetchServiceDocsData = useCallback(async () => {
    if (!serviceName || !serviceVersion) {
      return null;
    }

    try {
      const response = await marketplaceApi.getServiceDocs({
        name: serviceName,
        version: serviceVersion,
      });

      const docsData = {
        docs: response.docs,
        organizationId: response.author.organizationId,
        serviceName: serviceName,
      };

      setServiceDocsData(docsData);

      // Build the base public URL for relative file references
      const name = serviceName.replace(/^test\./, '');
      const publicUrl = `hub-service/${response.author.organizationId}/${name}/${serviceVersion}/docs`;
      setBasePublicUrl(publicUrl);

      return docsData;
    } catch (error) {
      console.error('Error fetching service docs:', error);
      return null;
    }
  }, [serviceName, serviceVersion]);


  /**
   * Fetches a file from the service 'docs' folder using relative paths.
   * Uses the base public URL established from the service docs data.
   */
  const fetchServiceDocsFile = useCallback(async (url: string) => {
    if (!basePublicUrl) {
      throw new Error('Base public URL not available. Service docs data may not be loaded yet.');
    }

    let finalUrl = url;
    // Handle relative URLs that point to files in the service docs folder
    if (url.startsWith(relativePathPrefix)) {
      finalUrl = `${marketplacePublicationUrl}/${basePublicUrl}/${url.replace(relativePathPrefix, '')}`;
    }

    const response = await fetch(finalUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${finalUrl}: ${response.status} ${response.statusText}`);
    }

    const text = await response.text();
    return text;
  }, [basePublicUrl]);

  /**
   * Intercept link clicks and open ":self:" links within this panel
   */
  const handleLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, rawUrl: string | undefined) => {
    // Handle anchor links (internal navigation within the same document)
    if (rawUrl?.startsWith('#')) {
      event.preventDefault();
      const targetId = rawUrl.substring(1);

      // Search within the content container to avoid conflicts with other page elements
      if (contentContainerRef.current) {
        const targetElement = contentContainerRef.current?.querySelector(`[id="${targetId}"]`);

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          return;
        } else {
          // Show error message if the anchor was not found
          message.config({
            getContainer: () => contentContainerRef.current as HTMLElement,
          });
          message.error(t('AnchorNotFoundWithName', { sectionName: targetId }));
          return;
        }
      }
    }

    if (rawUrl?.startsWith(relativePathPrefix) && rawUrl?.endsWith(SELF_NAVIGATION_SUFFIX)) {
      const url = rawUrl.replace(SELF_NAVIGATION_SUFFIX, '');
      const fetchNow = async () => {
        onLoadingContent(true);
        const text = await fetchServiceDocsFile(url);
        setHelpContent(text, '');
        onLoadingContent(false);
      };

      event.preventDefault();
      fetchNow();
    }
  }, [fetchServiceDocsFile, setHelpContent, onLoadingContent, t]);


  /**
   * Loads help documentation content. First tries to use the content from 
   * getServiceDocs response (for main service.md), then falls back to fetching
   * individual files for custom mdHelp paths.
   */
  const loadHelpDocs = useCallback(async () => {
    if (!state.service?.mdHelp) {
      return;
    }

    try {
      onLoadingContent(true);

      // Clear the history when loading new docs
      contentHistoryRef.current = [];

      // First ensure we have service docs data
      let docsData = serviceDocsData?.serviceName !== serviceName ? null : serviceDocsData;
      if (!docsData) {
        docsData = await fetchServiceDocsData();
        if (!docsData) {
          throw new Error('Failed to fetch service documentation data');
        }
      }

      // Fetch the help documentation content. for Services, this fetches the service.md file.
      const text = await fetchServiceDocsFile(state.service?.mdHelp);

      setHelpContent(text, '');
    } catch (error) {
      console.error('Error loading help docs', error);
      setHelpContent(`<h2>${t('Error.Title')} 😨</h2><p style="color: red;">${error?.message || t('Error.DefaultMessage')}</p>`, '');
    } finally {
      onLoadingContent(false);
    }
  }, [state.service?.mdHelp, serviceDocsData, fetchServiceDocsData, fetchServiceDocsFile, setHelpContent, t, onLoadingContent, serviceName]);

  useEffect(() => {
    setBasePublicUrl(null);
    setServiceDocsData(null);
    setCurrentContent(null);
    contentHistoryRef.current = [];
  }, [serviceName]);

  useEffect(() => {
    if (state.service?.mdHelp) {
      loadHelpDocs();
    }
  }, [loadHelpDocs, state.service?.mdHelp, serviceName]);

  useEffect(() => {
    onShowBackButton(contentHistoryRef.current.length > 1);
  }, [currentContent, onShowBackButton]);

  return (
    currentContent && basePublicUrl && (
      <div ref={contentContainerRef} className={styles.Container}>
        <MarkdownViewer
          disablePermalink
          markdown={currentContent}
          docsUrl={basePublicUrl}
          onLinkClick={handleLinkClick}
          onDraggingKemuSnippet={onDraggingKemuSnippet}
        />
      </div>
    )
  );
});

export default HubServiceHelpContent;
